import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Receipt,
  Users,
  Settings,
  Plus,
  Briefcase,
  Package,
  ClipboardList,
  Send,
} from 'lucide-react';
import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAccountantRole } from '@/hooks/useRole';
import { useAccountantPendingCount } from '@/hooks/useAccountantPendingCount';

const documentsItems = [
  { href: '/invoices', label: 'nav.invoices', icon: FileText },
  { href: '/quotes', label: 'nav.quotes', icon: ClipboardList },
  { href: '/expenses', label: 'nav.expenses', icon: Receipt },
];

const registryItems = [
  { href: '/clients', label: 'nav.clients', icon: Users },
  { href: '/suppliers', label: 'nav.suppliers', icon: Briefcase },
  { href: '/products', label: 'nav.products', icon: Package },
];

const quickActions = [
  { route: '/invoices', label: 'nav.newInvoice', icon: FileText },
  { route: '/quotes', label: 'nav.newQuote', icon: ClipboardList },
  { route: '/expenses', label: 'nav.newExpense', icon: Receipt },
];

export function BottomNav() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { isAccountant, isLoading } = useAccountantRole();
  const { receivedPendingCount, sentPendingCount } = useAccountantPendingCount();

  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  // Check if any route in a group is active
  const isDocumentsActive = documentsItems.some((item) => location.pathname === item.href);
  const isRegistryActive = registryItems.some((item) => location.pathname === item.href);

  const handleQuickAction = (route: string) => {
    setActionsOpen(false);
    // Navigate to /new page for creating new records
    navigate(`${route}/new`);
  };

  if (isLoading) return null;

  if (isAccountant) {
    const accountantNavClass = ({ isActive }: { isActive: boolean }) =>
      cn(
        'flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg transition-colors min-w-[56px] touch-manipulation',
        isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      );

    return (
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t">
        <div className="flex items-center justify-around h-16 px-2 safe-area-inset-bottom">
          {/* Overview */}
          <NavLink to="/accountant" end className={accountantNavClass}>
            {({ isActive }) => (
              <>
                <LayoutDashboard className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
                <span className="text-[10px] font-medium leading-none">{t('nav.accountantOverview')}</span>
              </>
            )}
          </NavLink>
          {/* Clients */}
          <NavLink to="/accountant/clients" className={accountantNavClass}>
            {({ isActive }) => (
              <>
                <Users className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
                <span className="text-[10px] font-medium leading-none">{t('nav.accountantClients')}</span>
              </>
            )}
          </NavLink>
          {/* Invitations */}
          <NavLink to="/accountant/pending" className={accountantNavClass}>
            {({ isActive }) => (
              <>
                <div className="relative">
                  <Send className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
                  {receivedPendingCount > 0 ? (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white">
                      {receivedPendingCount > 9 ? '9+' : receivedPendingCount}
                    </span>
                  ) : sentPendingCount > 0 ? (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[9px] font-bold text-white">
                      {sentPendingCount > 9 ? '9+' : sentPendingCount}
                    </span>
                  ) : null}
                </div>
                <span className="text-[10px] font-medium leading-none">{t('nav.accountantInvitations')}</span>
              </>
            )}
          </NavLink>
          {/* Settings */}
          <NavLink to="/settings" className={accountantNavClass}>
            {({ isActive }) => (
              <>
                <Settings className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
                <span className="text-[10px] font-medium leading-none">{t('nav.settings')}</span>
              </>
            )}
          </NavLink>
        </div>
      </nav>
    );
  }

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t">
      <div className="flex items-center justify-around h-16 px-2 safe-area-inset-bottom">
        {/* 1. Dashboard */}
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px] touch-manipulation',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )
          }
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
              <span className="text-[10px] font-medium leading-none">
                {t('nav.dashboard')}
              </span>
            </>
          )}
        </NavLink>

        {/* 2. Documents Popover */}
        <Popover open={documentsOpen} onOpenChange={setDocumentsOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px] touch-manipulation',
                isDocumentsActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label={t('nav.documents')}
            >
              <FileText className={cn('h-5 w-5', isDocumentsActive && 'fill-primary/20')} />
              <span className="text-[10px] font-medium leading-none">
                {t('nav.documents')}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="w-48 p-2">
            <div className="grid gap-1">
              {documentsItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setDocumentsOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors touch-manipulation',
                      'hover:bg-accent hover:text-accent-foreground',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t(item.label)}</span>
                  </NavLink>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        {/* 3. Quick Actions FAB */}
        <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center justify-center w-14 h-14 -mt-6 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors touch-manipulation"
              aria-label={t('nav.newItem')}
            >
              <Plus className="h-6 w-6" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="w-56 p-2">
            <div className="grid gap-1">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.route}
                    onClick={() => handleQuickAction(action.route)}
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors touch-manipulation hover:bg-accent hover:text-accent-foreground text-left"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t(action.label)}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        {/* 4. Registry Popover */}
        <Popover open={registryOpen} onOpenChange={setRegistryOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px] touch-manipulation',
                isRegistryActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label={t('nav.registry')}
            >
              <Users className={cn('h-5 w-5', isRegistryActive && 'fill-primary/20')} />
              <span className="text-[10px] font-medium leading-none">
                {t('nav.registry')}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="w-48 p-2">
            <div className="grid gap-1">
              {registryItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setRegistryOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors touch-manipulation',
                      'hover:bg-accent hover:text-accent-foreground',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t(item.label)}</span>
                  </NavLink>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        {/* 5. Settings */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors min-w-[56px] touch-manipulation',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )
          }
        >
          {({ isActive }) => (
            <>
              <Settings className={cn('h-5 w-5', isActive && 'fill-primary/20')} />
              <span className="text-[10px] font-medium leading-none">
                {t('nav.settings')}
              </span>
            </>
          )}
        </NavLink>
      </div>
    </nav>
  );
}
